import { useQuery } from '@apollo/client';
import { useRouter } from '@application/utils';
import { graphql } from '@gql/gql';
import type { BreedListGetAllBreedsQuery, BreedListGetAllBreedsQueryVariables } from '@gql/graphql';
import { SearchOption } from '@presentation/components';
import type { SelectOption } from '@presentation/components';
import { PLACEHOLDER_IMAGE_DOG } from '@presentation/constants';
import { filter, sortBy } from 'lodash/fp';
import { useMemo } from 'react';

export const BREED_LIST_GET_ALL_BREEDS = graphql(`
  query BreedListGetAllBreeds(
    $where: DogBreedWhereInput
    $translationsWhere: DogBreedTranslationWhereInput
  ) {
    findManyDogBreed(where: $where) {
      id
      name
      baseBreedName
      translations(where: $translationsWhere) {
        id
        name
        otherNames
        url
      }
      imgs {
        id
        small
      }
    }
  }
`);

export const useGetAllBreeds = () => {
  const { language } = useRouter();

  const variables = useMemo((): BreedListGetAllBreedsQueryVariables => {
    return {
      translationsWhere: {
        AND: [
          { language: { equals: language } },
          { description: { not: null } },
          { url: { not: null } },
        ],
      },
    };
  }, [language]);

  const { data, loading, error } = useQuery<
    BreedListGetAllBreedsQuery,
    BreedListGetAllBreedsQueryVariables
  >(BREED_LIST_GET_ALL_BREEDS, {
    variables,
  });

  const breedListOptionsWithImages: SelectOption<string>[] = useMemo(() => {
    const filteredBreeds = filter((b) => !!b.translations?.[0]?.url, data?.findManyDogBreed ?? []);

    return sortBy((b) => b.translations?.[0]?.name?.toLocaleLowerCase(), filteredBreeds).map(
      (breed) => {
        const searchValues = [
          ...breed.translations[0].otherNames,
          breed.name ?? breed.baseBreedName,
          breed.translations[0].name,
        ].filter((x) => !!x);
        return {
          value: breed.translations[0].url,
          searchValues,
          label: (
            <SearchOption
              title={breed.translations[0].name}
              image={breed.imgs?.[0]?.small ?? PLACEHOLDER_IMAGE_DOG}
            />
          ),
        } as SelectOption<string>;
      },
    );
  }, [data?.findManyDogBreed]);

  return {
    breedsLoading: loading,
    breedsError: error,
    breeds: data?.findManyDogBreed ?? [],
    breedListOptionsWithImages,
  };
};
